import { create } from 'zustand';
import { Timestamp, type Unsubscribe } from '@firebase/firestore';
import { Structure, StructureClaim, StakeholderType, MapClaimSummary } from '../types';
import { structuresApi } from '../api/structures';
import { structureClaimsApi } from '../api/structureClaims';
import { subscribeToAreaClaimSummaries } from '../api/claimSummaries';
import { subscribeToUserAddedStructures } from '../api/userStructures';
import { structuresGeojsonApi, isAbortError, type AreaManifest } from '../api/structuresGeojson';
import { readCachedShard, writeCachedShard, evictStaleShards } from '../api/shardCache';
import {
  callAddUserStructure,
  type AddUserStructureInput,
  type AddUserStructureResult,
} from '../api/userStructures';
import { startSpan, endSpan } from '../utils/mapPerf';
import { AppError } from '../utils/errorHandler';
import { useServiceAreasStore } from './serviceAreasStore';
import { useAuthStore } from './authStore';
import { withLoading } from './utils/withLoading';
import { createPrefixedLogger } from '../utils/logger';
import {
  joinClaim,
  findStructureInShards,
  findUserAdded,
  sameIds,
  selectShardCandidates,
  selectMissingAreaShards,
  evictToLimit,
  SHARD_FETCH_CONCURRENCY,
  MAX_SHARDS_PER_LOAD,
  SEARCH_PRELOAD_MAX_STRUCTURES,
  type ShardCandidate,
} from './structures/shardLoader';
import { searchStructuresPure } from './structures/searchStructures';
import {
  mergeClaimSummaries,
  pruneOptimisticClaims,
  type OptimisticClaimEntry,
} from './structures/claimMerge';

// Re-export joinClaim so existing consumers (Map.native.tsx, Map.web.tsx)
// that import it from this module continue to work without edits.
export { joinClaim } from './structures/shardLoader';

const log = createPrefixedLogger('store:structures');

interface ViewportBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

interface LoadAreasArgs extends ViewportBox {
  areaIds: string[];
}

interface StructuresState {
  // Data
  selectedStructure: Structure | null;
  selectedClaim: StructureClaim | null;
  claimedStructureIds: string[];
  claims: Record<string, StructureClaim>;
  // Live claim state across subscribed areas — the merged result of the
  // per-area claimSummaries baseline (`_areaClaimsByChunk`), this user's own
  // claims (`_ownClaims`), and the local optimistic overlay
  // (`_optimisticClaims`). See structures/claimMerge.ts and
  // `recomputeClaims` below for the merge order. Never write this field
  // directly outside `recomputeClaims` — every mutation path routes through
  // it so a subsequent snapshot fire can't clobber an optimistic write.
  claimsByStructureId: Record<string, MapClaimSummary>;
  searchResults: Structure[];

  // Loading states
  isLoading: boolean;
  isSearching: boolean;
  isClaiming: boolean;
  error: string | null;

  // Structures keyed by `${areaId}:${shardId}`. The UI never sees a flat
  // array — the map iterates these shard buckets at viewport-filter time
  // (with `_manifestsByArea` used as a coarse bbox prefilter) and search
  // iterates them too. That way neither the shard-commit path nor the claims
  // subscription needs to allocate an 80k-entry array each time.
  _manifestsByArea: Record<string, AreaManifest>;
  _structuresByShard: Record<string, Structure[]>;
  _loadedShardOrder: string[]; // LRU: oldest first, newest last
  _inflightShards: Set<string>;
  _loadedAreaIds: string[]; // derived from _manifestsByArea keys
  _subscribedAreaIds: string[]; // sorted; matches `_areaClaimsUnsub` scope

  // Monotonic counter bumped on every `loadAreasForViewport` call. The
  // previous call's AbortController is aborted so its in-flight shard and
  // manifest fetches cancel, and any that still resolve check this
  // generation before committing to state.
  _loadGeneration: number;
  _loadAbortController: AbortController | null;

  // Center of the most recently requested viewport (set at the top of every
  // `loadAreasForViewport` call, win or lose the generation race). The
  // search preload (`ensureAreaLoadedForSearch`) has no viewport of its own
  // — it reuses this as the eviction center so a cap-triggered eviction (see
  // evictToLimit) still favors whatever's on screen over the far side of the
  // area it's sweeping. Null until the first viewport load.
  _lastViewportCenter: { lat: number; lng: number } | null;

  // One entry per areaId currently being swept by `ensureAreaLoadedForSearch`
  // — lets a second call for the same area while one is in flight await the
  // same promise instead of starting a duplicate sweep.
  _searchPreloadInFlight: Record<string, Promise<void> | undefined>;

  // Subscriptions
  _claimUnsub: Unsubscribe | null;
  // One unsub per subscribed area's claimSummaries doc.
  _areaClaimsUnsubs: Unsubscribe[];
  // Summaries keyed by areaId so we can merge snapshots from multiple
  // area subscriptions into one `claimsByStructureId`.
  _areaClaimsByChunk: Record<string, Record<string, MapClaimSummary>>;

  // This user's own claims, from a direct `structureClaims` subscription
  // (not CF-derived, so it reflects this user's claim/unclaim writes and
  // claims made on another device faster than the area baseline above).
  _ownClaims: Record<string, MapClaimSummary>;
  _ownClaimsUnsub: Unsubscribe | null;

  // Local claim/unclaim overlay written synchronously the instant the user
  // acts (claimStructure/unclaimStructure/addUserStructure), before either
  // subscription above has had a chance to fire. See claimMerge.ts.
  _optimisticClaims: Record<string, OptimisticClaimEntry>;

  // Live user-added structures (global, not scoped by service area).
  // Service areas are kept as a backend concept only for now — see
  // subscribeToUserAddedStructures. Merged into search, fetchStructure,
  // and the map renderer so a freshly added structure surfaces without
  // waiting for the next GeoJSON export.
  userAddedStructures: Structure[];
  _userAddedUnsub: Unsubscribe | null;

  // Actions
  loadAreasForViewport: (args: LoadAreasArgs) => Promise<void>;
  subscribeToAreaClaims: (serviceAreaIds: string[]) => void;
  unsubscribeAreaClaims: () => void;
  subscribeToUserAdded: () => void;
  unsubscribeUserAdded: () => void;
  /**
   * Live-subscribe to this user's own claims (`structureClaims` where
   * `claimedBy == userId`). Idempotent — mirrors `subscribeToUserAdded`.
   * Feeds `_ownClaims`, which `recomputeClaims` merges ahead of the local
   * optimistic overlay so a claim/unclaim made on another device (or
   * confirmed server-side) shows up without waiting on the CF-derived area
   * baseline.
   */
  subscribeOwnClaims: (userId: string) => void;
  unsubscribeOwnClaims: () => void;
  fetchStructure: (structureId: string) => Promise<Structure | null>;
  fetchClaimedStructures: (userId: string) => Promise<void>;
  subscribeToClaim: (structureId: string) => void;
  unsubscribeClaim: () => void;
  claimStructure: (
    structureId: string,
    userId: string,
    options?: { insurerChecklistId?: string; stakeholderType?: StakeholderType }
  ) => Promise<void>;
  unclaimStructure: (structureId: string, userId: string) => Promise<void>;
  updateInsuranceAssignment: (
    structureId: string,
    userId: string,
    assignment: { insurerChecklistId?: string | null; stakeholderType?: StakeholderType | null }
  ) => Promise<void>;
  searchStructures: (query: string, options?: { areaId?: string }) => Promise<void>;
  selectStructure: (structure: Structure | null) => void;
  clearSearch: () => void;
  clearError: () => void;
  /**
   * Session teardown, called by authStore.signOut: clear the selected
   * structure so a stale selection doesn't reopen the sheet under the new
   * (anon) user. Map shards/claims subscriptions are left alone — the map
   * screen owns their lifecycle.
   */
  reset: () => void;
  /**
   * Drop in-memory manifests, shards, and loaded-area bookkeeping for any
   * areaId not in `activeAreaIds`. Used when an area flips to inactive and
   * we need to stop showing its cached pins — the persistent shard cache
   * (IndexedDB) is untouched so shards can be re-used if the area is
   * reactivated later.
   */
  evictAreasNotIn: (activeAreaIds: string[]) => void;
  /**
   * Fetch area manifests without pulling any shards. Used at wide zoom so
   * we can render the per-shard overview (bbox centroid + count) without
   * paying the full-area-data cost.
   */
  ensureManifests: (areaIds: string[]) => Promise<void>;
  /**
   * Sweep the rest of ONE area's shards in the background so address search
   * (which only scans shards already in `_structuresByShard`) can find a
   * match the viewport hasn't panned to yet. Fetches in batches of at most
   * MAX_SHARDS_PER_LOAD, one batch after another, and is a no-op above
   * SEARCH_PRELOAD_MAX_STRUCTURES (checked against the area's manifest,
   * fetched first via `ensureManifests` if not already loaded). Idempotent:
   * a second call for the same area while one is in flight awaits the same
   * promise; a call once everything is loaded resolves immediately having
   * fetched nothing. Deliberately does NOT touch `_loadGeneration` /
   * `_loadAbortController` — see the comment on the action's implementation
   * for why a concurrent viewport load and this preload can't starve each
   * other.
   */
  ensureAreaLoadedForSearch: (areaId: string) => Promise<void>;
  /**
   * Fetch multiple structures by id without touching selection state.
   * Used where the caller maintains its own local list (e.g. profile
   * screen's "My Properties" preview). Each missing/erroring id is
   * silently skipped so the caller receives whatever loaded.
   */
  fetchStructuresByIds: (ids: string[]) => Promise<Structure[]>;
  /**
   * Fetch a single structure by id without touching selection state or
   * triggering any loading flags. The focus-structure callback on the map
   * uses a latest-wins counter that must NOT be cancelled by effect
   * cleanup — callers are responsible for managing their own cancellation
   * if needed.
   */
  getStructureById: (structureId: string) => Promise<Structure | null>;
  /**
   * Read the owner-private recipient list for a claim subdoc. Thin
   * pass-through to structureClaimsApi.getReportRecipients — kept here
   * so components never import the api layer directly.
   */
  getReportRecipients: (structureId: string) => Promise<string[]>;
  /**
   * Write the report-recipient list for a claim. Thin pass-through to
   * structureClaimsApi.updateReportRecipients — kept here so components
   * never import the api layer directly. Rethrows on failure (callers
   * show their own error UI).
   */
  updateReportRecipients: (
    structureId: string,
    userId: string,
    recipients: string[]
  ) => Promise<void>;
  /**
   * Write the default post-visibility setting for a claim. Thin
   * pass-through; rethrows on failure.
   */
  updateDefaultVisibility: (
    structureId: string,
    userId: string,
    visibility: import('../types').PostVisibility
  ) => Promise<void>;
  /**
   * Fetch the claim doc for a given structure without touching selection
   * state. Returns null if no claim exists. Used by screens that need
   * claim data for seeding UI defaults (e.g. defaultPostVisibility).
   */
  getClaimForStructure: (structureId: string) => Promise<import('../types').StructureClaim | null>;
  /**
   * Add a user-created structure via the Cloud Function callable. Thin
   * pass-through to callAddUserStructure — keeps components away from
   * the api layer. Rethrows on failure so the caller can surface an error.
   */
  addUserStructure: (input: AddUserStructureInput) => Promise<AddUserStructureResult>;
  /**
   * "Notify my agent": thin pass-through to structureClaimsApi.notifyAgent.
   * Optimistically patches evaluationStatus (+ a client-side
   * evaluationStatusAt) onto `claims[structureId]` and `selectedClaim` when
   * it matches — the same two places updateInsuranceAssignment patches.
   * Deliberately NOT `claimsByStructureId`: that map is the pin-coloring
   * merge (area baseline + own-claims + a claim/unclaim optimistic overlay
   * with its own TTL/retirement rules in structures/claimMerge.ts) and an
   * owner who can call this already has an entry there from `_ownClaims`,
   * so a claim/unclaim-shaped overlay would be retired before it was ever
   * read. Rethrows on failure so the caller can surface an error.
   */
  notifyAgent: (
    structureId: string,
    status: import('../types').EvaluationStatus,
    note?: string
  ) => Promise<import('../types/api').NotifyAgentResult>;
}

/**
 * Private helper, following the same pattern as `withLoading` (a store
 * utility that takes `set`/`get` explicitly rather than closing over them):
 * recompute `claimsByStructureId` from the three claim sources and preserve
 * the selectedStructure re-join so an open detail sheet updates. Every claim
 * mutation path (area snapshot, claim/unclaim, addUserStructure, own-claims
 * snapshot) routes through this instead of writing `claimsByStructureId`
 * directly — that direct-write-then-clobber was the bug this fixes.
 */
function recomputeClaims(
  set: (partial: Partial<StructuresState>) => void,
  get: () => StructuresState
): void {
  const state = get();
  const now = Date.now();
  const merged = mergeClaimSummaries(
    state._areaClaimsByChunk,
    state._ownClaims,
    state._optimisticClaims,
    now
  );
  const prunedOptimistic = pruneOptimisticClaims(state._ownClaims, state._optimisticClaims, now);

  const patch: Partial<StructuresState> = { claimsByStructureId: merged };
  if (prunedOptimistic !== state._optimisticClaims) {
    patch._optimisticClaims = prunedOptimistic;
  }
  // Refresh the selected structure's claimSummary in place if its claim
  // just changed — the detail sheet reads it off this object.
  if (state.selectedStructure) {
    const claim = merged[state.selectedStructure.id];
    const joined = joinClaim(state.selectedStructure, claim);
    if (joined !== state.selectedStructure) patch.selectedStructure = joined;
  }
  set(patch);
}

/**
 * Fetch a batch of shards with bounded concurrency (cache-first, falling
 * through to network). Shared by `loadAreasForViewport` and
 * `ensureAreaLoadedForSearch` so there's exactly one place that knows how a
 * shard gets onto the wire — the two callers differ only in how they choose
 * `toFetch` and whether they check staleness before committing (see
 * `commitFetchedShards` and the comment on `ensureAreaLoadedForSearch`).
 * Does not touch `_inflightShards` or commit anything to state; the caller
 * owns both, since the viewport path needs to bail on a stale generation
 * before committing while the preload path never does.
 */
async function fetchShardBatch(
  get: () => StructuresState,
  toFetch: ShardCandidate[],
  signal?: AbortSignal
): Promise<{ staged: { key: string; structs: Structure[] }[]; failures: string[] }> {
  const staged: { key: string; structs: Structure[] }[] = [];
  const failures: string[] = [];
  let idx = 0;
  const workers: Promise<void>[] = [];
  const workerCount = Math.min(SHARD_FETCH_CONCURRENCY, toFetch.length);
  for (let w = 0; w < workerCount; w++) {
    workers.push(
      (async () => {
        while (true) {
          if (signal?.aborted) return;
          const my = idx++;
          if (my >= toFetch.length) return;
          const { areaId, shardId, key } = toFetch[my];
          const manifest = get()._manifestsByArea[areaId];
          const generatedAt = manifest?.generatedAt ?? '';
          // Cache-first read, bounded to 50ms. Falls through to network
          // on miss, parse error, or slow disk.
          if (generatedAt) {
            try {
              const cached = await readCachedShard(areaId, shardId, generatedAt);
              if (cached) {
                staged.push({ key, structs: cached });
                continue;
              }
            } catch {
              // Cache failures must never block the load.
            }
          }
          try {
            const structs = await structuresGeojsonApi.loadShard(areaId, shardId, signal);
            staged.push({ key, structs });
            if (generatedAt) writeCachedShard(areaId, shardId, generatedAt, structs);
          } catch (err) {
            if (isAbortError(err)) return;
            log.error(`Failed to load shard ${key}`, err);
            failures.push(key);
          }
        }
      })()
    );
  }
  await Promise.all(workers);
  return { staged, failures };
}

/**
 * Merge a fetched batch into `_structuresByShard`, run LRU eviction once
 * over the final totals, and clear `_inflightShards` for the batch's keys.
 * Single commit, same as the old inline version in `loadAreasForViewport` —
 * one re-render (and one Leaflet cluster-tree rebuild) per batch instead of
 * one per shard. Shared with `ensureAreaLoadedForSearch`, which has no
 * viewport of its own and passes `_lastViewportCenter` (or the fallback
 * origin) as the eviction center — see that action for why that's safe.
 */
function commitFetchedShards(
  set: (partial: Partial<StructuresState>) => void,
  get: () => StructuresState,
  toFetch: ShardCandidate[],
  staged: { key: string; structs: Structure[] }[],
  viewportCenterLat: number,
  viewportCenterLng: number
): void {
  const current = get();
  const stagedByShard = { ...current._structuresByShard };
  const stagedByKey = new Map<string, Structure[]>();
  for (const s of staged) stagedByKey.set(s.key, s.structs);
  const newKeys: string[] = [];
  for (const item of toFetch) {
    const structs = stagedByKey.get(item.key);
    if (!structs) continue;
    stagedByShard[item.key] = structs;
    newKeys.push(item.key);
  }

  const { byShard: nextByShard, order: nextOrder } = evictToLimit({
    byShard: stagedByShard,
    currentOrder: current._loadedShardOrder,
    newKeys,
    viewportCenterLat,
    viewportCenterLng,
    manifests: get()._manifestsByArea,
  });

  const nextInflight = new Set(current._inflightShards);
  for (const item of toFetch) nextInflight.delete(item.key);

  set({
    _structuresByShard: nextByShard,
    _loadedShardOrder: nextOrder,
    _inflightShards: nextInflight,
  });
}

export const useStructuresStore = create<StructuresState>((set, get) => ({
  selectedStructure: null,
  selectedClaim: null,
  claimedStructureIds: [],
  claims: {},
  claimsByStructureId: {},
  searchResults: [],
  isLoading: false,
  isSearching: false,
  isClaiming: false,
  error: null,
  _manifestsByArea: {},
  _structuresByShard: {},
  _loadedShardOrder: [],
  _inflightShards: new Set(),
  _loadedAreaIds: [],
  _subscribedAreaIds: [],
  _loadGeneration: 0,
  _loadAbortController: null,
  _lastViewportCenter: null,
  _searchPreloadInFlight: {},
  _claimUnsub: null,
  _areaClaimsUnsubs: [],
  _areaClaimsByChunk: {},
  _ownClaims: {},
  _ownClaimsUnsub: null,
  _optimisticClaims: {},
  userAddedStructures: [],
  _userAddedUnsub: null,

  loadAreasForViewport: async ({ areaIds, south, west, north, east }: LoadAreasArgs) => {
    // Abort the previous call's in-flight fetches. Fast pans no longer queue
    // up stale manifest + shard requests that commit late and trigger
    // phantom cluster rebuilds.
    const prior = get();
    prior._loadAbortController?.abort();
    const controller = new AbortController();
    const myGeneration = prior._loadGeneration + 1;
    // Recorded win or lose the generation race — this is the only viewport
    // the search preload (ensureAreaLoadedForSearch) has to go on for
    // eviction, and even a call that ends up stale reflects where the user
    // is looking right now.
    const viewportCenterLat = (south + north) / 2;
    const viewportCenterLng = (west + east) / 2;
    set({
      _loadGeneration: myGeneration,
      _loadAbortController: controller,
      _lastViewportCenter: { lat: viewportCenterLat, lng: viewportCenterLng },
    });
    const { signal } = controller;
    const isStale = () => get()._loadGeneration !== myGeneration;

    // Phase 1: make sure we have a manifest for each active area. Manifests
    // are tiny (sub-100KB) so there's no cost to pulling them eagerly as soon
    // as an area enters the viewport.
    const existingManifests = get()._manifestsByArea;
    const manifestsToFetch = areaIds.filter((id) => !(id in existingManifests));
    if (manifestsToFetch.length > 0) {
      const results = await Promise.allSettled(
        manifestsToFetch.map((id) => structuresGeojsonApi.loadAreaManifest(id, signal))
      );
      if (isStale()) return;
      const nextManifests = { ...get()._manifestsByArea };
      const manifestFailures: string[] = [];
      results.forEach((r, i) => {
        const id = manifestsToFetch[i];
        if (r.status === 'fulfilled') {
          nextManifests[id] = r.value;
        } else if (!isAbortError(r.reason)) {
          log.error(`Failed to load manifest for ${id}`, r.reason);
          manifestFailures.push(id);
        }
      });
      set({
        _manifestsByArea: nextManifests,
        _loadedAreaIds: Object.keys(nextManifests),
      });
      // Fire-and-forget: drop cached shards whose area manifest generation
      // changed. Keeps the disk cache from growing forever across deploys.
      for (const id of manifestsToFetch) {
        const m = nextManifests[id];
        if (m) void evictStaleShards(id, m.generatedAt);
      }
      if (manifestFailures.length > 0 && manifestFailures.length === manifestsToFetch.length) {
        const first = results.find(
          (r) => r.status === 'rejected' && !isAbortError((r as PromiseRejectedResult).reason)
        ) as PromiseRejectedResult | undefined;
        if (first) {
          set({ error: `Couldn't load ${manifestFailures.join(', ')}: ${first.reason}` });
        }
        return;
      }
      if (manifestFailures.length > 0) {
        set({ error: `Couldn't load manifests: ${manifestFailures.join(', ')}` });
      }
    }

    // Phase 2: pick shards whose bbox intersects the viewport. Skip any shard
    // already loaded or currently being fetched. Touching an already-loaded
    // shard counts as a cache "hit" and bumps it to the end of the LRU so
    // shards currently in view never get evicted before offscreen ones.
    const viewport: ViewportBox = { south, west, north, east };
    const { _manifestsByArea, _loadedShardOrder, _inflightShards } = get();
    // Fetch closest-first, capped per call so wide viewports don't queue up
    // thousands of requests. Subsequent pan/zoom will re-prioritize.
    const { toFetch, touchedKeys } = selectShardCandidates({
      areaIds,
      manifests: _manifestsByArea,
      loadedShardOrder: _loadedShardOrder,
      inflightShards: _inflightShards,
      viewport,
    });

    // Promote touched shards to the end of the LRU queue even when there's
    // nothing new to fetch — keeps in-viewport shards from aging out.
    if (touchedKeys.length > 0) {
      const touchedSet = new Set(touchedKeys);
      const order = _loadedShardOrder.filter((k) => !touchedSet.has(k));
      for (const k of touchedKeys) order.push(k);
      if (order.length !== _loadedShardOrder.length || touchedKeys.length > 0) {
        set({ _loadedShardOrder: order });
      }
    }

    if (toFetch.length === 0) return;

    // Mark inflight so concurrent viewport-change effects don't double-fetch.
    const inflightBefore = new Set(_inflightShards);
    for (const item of toFetch) inflightBefore.add(item.key);
    set({ _inflightShards: inflightBefore });

    // Phase 3: fetch shards with bounded concurrency (fetchShardBatch), then
    // commit once (commitFetchedShards) so the map only re-renders (and
    // Leaflet only rebuilds its cluster tree) a single time per viewport
    // load — instead of once per shard, which compounded badly with the
    // 8-way concurrency. Both helpers are shared with
    // ensureAreaLoadedForSearch; see the comment there for why a concurrent
    // preload and viewport load can't starve each other.
    const loadSpan = startSpan('shards.load');
    await withLoading(set, 'isLoading', async () => {
      const { staged, failures } = await fetchShardBatch(get, toFetch, signal);

      // A newer load has started while we were fetching. Clear our inflight
      // markers so the new generation can reissue any it still needs, and
      // bail without committing — the new generation owns the state.
      if (isStale()) {
        const nextInflight = new Set(get()._inflightShards);
        for (const item of toFetch) nextInflight.delete(item.key);
        set({ _inflightShards: nextInflight });
        return;
      }

      // Evict by distance from the viewport center, farthest first. "Oldest"
      // was the wrong policy here — when a user pans across a continental
      // view and two metros (BBL + SF) both want to load, FIFO eviction
      // dropped whichever was alphabetically first, leaving just one cluster
      // visible. Distance-based eviction keeps whatever's under the user's
      // cursor, which is what they actually want on screen.
      commitFetchedShards(set, get, toFetch, staged, viewportCenterLat, viewportCenterLng);

      if (failures.length > 0 && failures.length < toFetch.length) {
        set({ error: `Couldn't load ${failures.length} of ${toFetch.length} shards` });
      }
      if (failures.length > 0 && failures.length === toFetch.length) {
        throw new Error(`All ${toFetch.length} shard loads failed`);
      }
    }).finally(() => {
      endSpan(loadSpan);
    });
  },

  subscribeToAreaClaims: (serviceAreaIds: string[]) => {
    const sorted = [...serviceAreaIds].sort();
    if (sameIds(sorted, get()._subscribedAreaIds) && get()._areaClaimsUnsubs.length > 0) return;

    for (const u of get()._areaClaimsUnsubs) u();

    if (sorted.length === 0) {
      set({
        _areaClaimsUnsubs: [],
        _areaClaimsByChunk: {},
        _subscribedAreaIds: [],
      });
      recomputeClaims(set, get);
      return;
    }

    // One claimSummaries doc subscription per area (cost fix F2): the
    // initial snapshot bills 1 read per area instead of one per claim doc
    // in the area, which dominated read costs at scale. The summary is
    // maintained by the onStructureClaimWritten Cloud Function; per-doc
    // claim subscriptions (detail sheets) still use subscribeToClaim.
    const unsubs: Unsubscribe[] = [];
    set({ _areaClaimsByChunk: {} });

    for (const areaId of sorted) {
      const unsub = subscribeToAreaClaimSummaries(
        areaId,
        (structures) => {
          const nextByArea = { ...get()._areaClaimsByChunk, [areaId]: structures };
          set({ _areaClaimsByChunk: nextByArea });
          recomputeClaims(set, get);
        },
        (err) => {
          log.error('Area claim summaries subscription error', err);
          set({ error: err.message });
        }
      );
      unsubs.push(unsub);
    }
    set({ _areaClaimsUnsubs: unsubs, _subscribedAreaIds: sorted });
  },

  unsubscribeAreaClaims: () => {
    for (const u of get()._areaClaimsUnsubs) u();
    set({
      _areaClaimsUnsubs: [],
      _areaClaimsByChunk: {},
      _subscribedAreaIds: [],
    });
    recomputeClaims(set, get);
  },

  subscribeToUserAdded: () => {
    // Idempotent: ignore re-subscribes from re-renders or screen remounts.
    if (get()._userAddedUnsub) return;
    const unsub = subscribeToUserAddedStructures(
      (structures) => set({ userAddedStructures: structures }),
      (err) => log.error('user-added subscription error', err)
    );
    set({ _userAddedUnsub: unsub });
  },

  unsubscribeUserAdded: () => {
    get()._userAddedUnsub?.();
    set({ _userAddedUnsub: null, userAddedStructures: [] });
  },

  subscribeOwnClaims: (userId: string) => {
    // Idempotent: ignore re-subscribes from re-renders or screen remounts.
    if (get()._ownClaimsUnsub) return;
    const unsub = structureClaimsApi.subscribeToUserClaims(
      userId,
      (claimsList) => {
        const ownClaims: Record<string, MapClaimSummary> = {};
        for (const c of claimsList) ownClaims[c.id] = c;
        set({ _ownClaims: ownClaims });
        recomputeClaims(set, get);
      },
      (err) => log.error('own-claims subscription error', err)
    );
    set({ _ownClaimsUnsub: unsub });
  },

  unsubscribeOwnClaims: () => {
    get()._ownClaimsUnsub?.();
    set({ _ownClaimsUnsub: null, _ownClaims: {} });
    recomputeClaims(set, get);
  },

  fetchStructure: async (structureId: string) => {
    return await withLoading(set, 'isLoading', async () => {
      // Prefer the live user-added cache when it has the doc — avoids a
      // round-trip and surfaces the fresh `verified` / `source` fields a
      // GeoJSON-sourced read wouldn't carry.
      const fromUserAdded = findUserAdded(get().userAddedStructures, structureId);
      const structure = fromUserAdded ?? (await structuresApi.getStructure(structureId));
      if (!structure) return null;
      // `structuresApi.getStructure` strips any stale `claimSummary` off
      // the server doc. Re-join with the live claim from the active
      // subscription so the detail sheet shows the real claim state.
      const joined = joinClaim(structure, get().claimsByStructureId[structureId]);
      set({ selectedStructure: joined });
      return joined;
    });
  },

  fetchClaimedStructures: async (userId: string) => {
    await withLoading(set, 'isLoading', async () => {
      const claimsList = await structureClaimsApi.getClaimedStructures(userId);
      const claims: Record<string, StructureClaim> = {};
      const ids: string[] = [];
      for (const c of claimsList) {
        claims[c.id] = c;
        ids.push(c.id);
      }
      set({ claims, claimedStructureIds: ids });
    });
  },

  subscribeToClaim: (structureId: string) => {
    get()._claimUnsub?.();
    const unsub = structureClaimsApi.subscribeToClaim(
      structureId,
      (claim) => {
        set({ selectedClaim: claim });
        if (claim) {
          set((s) => ({ claims: { ...s.claims, [structureId]: claim } }));
        }
      },
      (err) => set({ error: err.message })
    );
    set({ _claimUnsub: unsub });
  },

  unsubscribeClaim: () => {
    get()._claimUnsub?.();
    set({ _claimUnsub: null, selectedClaim: null });
  },

  claimStructure: async (structureId, userId, options) => {
    await withLoading(set, 'isClaiming', async () => {
      // firestore.rules rejects anonymous claim creates; fail here with
      // friendly copy so any call path that skips the UI's upgrade prompt
      // doesn't surface a raw permission error.
      if (useAuthStore.getState().user?.isAnonymous) {
        throw new AppError('Save your account to claim a property.', 'PERMISSION_DENIED');
      }
      // Shards are viewport-driven, so a structure reached by deep link
      // (share link straight to /structure/<id>) is often in neither the
      // shard cache nor selectedStructure — fall back to fetching the doc
      // before declaring it unknown.
      const structure =
        findStructureInShards(get()._structuresByShard, structureId) ??
        (get().selectedStructure?.id === structureId ? get().selectedStructure : null) ??
        (await structuresApi.getStructure(structureId));
      if (!structure) {
        throw new AppError('Cannot claim unknown structure', 'NOT_FOUND');
      }
      // Service areas scope, they never block (CLAUDE.md hard rule 6). A
      // structure no active area contains (a user-added home in Tallahassee
      // while Leon County is inactive, say) keeps the area its own doc
      // carries, which addUserStructure sets to 'unassigned' for exactly this
      // case. Rules only require the field to be a string; the summary
      // trigger keys on it and tolerates an unknown area.
      const area = useServiceAreasStore
        .getState()
        .findContaining(structure.centroid.latitude, structure.centroid.longitude);
      const serviceAreaId = area?.id ?? structure.serviceAreaId ?? 'unassigned';

      await structureClaimsApi.claimStructure(structureId, userId, serviceAreaId, options);

      const { claimedStructureIds } = get();

      const optimisticClaim: StructureClaim = {
        id: structureId,
        claimedBy: userId,
        claimedAt: Timestamp.now(),
        serviceAreaId,
        stakeholderType: options?.stakeholderType,
        insurerChecklistId: options?.insurerChecklistId,
        updatedAt: Timestamp.now(),
      };

      // Optimistic overlay instead of a direct claimsByStructureId write —
      // recomputeClaims owns that field so a subsequent area-summary or
      // own-claims snapshot can't clobber it (the bug this refactor fixes).
      set((s) => ({
        _optimisticClaims: {
          ...s._optimisticClaims,
          [structureId]: { summary: optimisticClaim, at: Date.now() },
        },
        selectedClaim: optimisticClaim,
        claims: { ...s.claims, [structureId]: optimisticClaim },
        claimedStructureIds: claimedStructureIds.includes(structureId)
          ? claimedStructureIds
          : [structureId, ...claimedStructureIds],
      }));
      recomputeClaims(set, get);
    });
  },

  updateInsuranceAssignment: async (structureId, userId, assignment) => {
    await withLoading(set, 'isClaiming', async () => {
      await structureClaimsApi.updateInsuranceAssignment(structureId, userId, assignment);
      const { selectedClaim } = get();
      if (selectedClaim?.id === structureId) {
        const patch: Partial<StructureClaim> = {};
        if (assignment.insurerChecklistId !== undefined) {
          patch.insurerChecklistId = assignment.insurerChecklistId ?? undefined;
        }
        if (assignment.stakeholderType !== undefined) {
          patch.stakeholderType = assignment.stakeholderType ?? undefined;
        }
        const updated = { ...selectedClaim, ...patch };
        set((s) => ({
          selectedClaim: updated,
          claims: { ...s.claims, [structureId]: updated },
        }));
      }
    });
  },

  unclaimStructure: async (structureId: string, userId: string) => {
    await withLoading(set, 'isClaiming', async () => {
      await structureClaimsApi.unclaimStructure(structureId, userId);

      const { claimedStructureIds } = get();

      // Tombstone overlay (null summary) instead of a direct
      // claimsByStructureId delete — recomputeClaims re-derives
      // selectedStructure's claimSummary from the merged result below.
      set((s) => {
        const { [structureId]: _droppedClaim, ...remainingClaims } = s.claims;
        return {
          _optimisticClaims: {
            ...s._optimisticClaims,
            [structureId]: { summary: null, at: Date.now() },
          },
          selectedClaim: null,
          claims: remainingClaims,
          claimedStructureIds: claimedStructureIds.filter((id) => id !== structureId),
        };
      });
      recomputeClaims(set, get);
    });
  },

  searchStructures: async (searchQuery: string, options?: { areaId?: string }) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      set({ searchResults: [] });
      return;
    }

    await withLoading(set, 'isSearching', async () => {
      const { _structuresByShard, userAddedStructures, claimsByStructureId } = get();
      const matches = searchStructuresPure({
        lower: trimmed.toLowerCase(),
        userAddedStructures,
        structuresByShard: _structuresByShard,
        claimsByStructureId,
        areaId: options?.areaId,
      });
      set({ searchResults: matches });
    });
  },

  selectStructure: (structure: Structure | null) => {
    // Selection payloads arrive un-joined — the marker click hands over the
    // raw shard copy and the focus path a stripped fetch — so join against
    // the merged claim truth at select time or the sheet shows "Claim
    // Property" on an already-claimed structure. recomputeClaims keeps the
    // selection fresh from then on.
    set({
      selectedStructure: structure
        ? joinClaim(structure, get().claimsByStructureId[structure.id])
        : null,
    });
  },

  reset: () => {
    // Session teardown: drop the previous identity's own-claims subscription
    // and both claim overlays so they can't leak into the next (possibly
    // anonymous) session. Map shards/area-claims subscriptions are left
    // alone — the map screen owns their lifecycle.
    get()._ownClaimsUnsub?.();
    set({
      selectedStructure: null,
      _ownClaims: {},
      _ownClaimsUnsub: null,
      _optimisticClaims: {},
    });
    recomputeClaims(set, get);
  },

  clearSearch: () => {
    set({ searchResults: [] });
  },

  clearError: () => {
    set({ error: null });
  },

  ensureManifests: async (areaIds: string[]) => {
    const existing = get()._manifestsByArea;
    const toFetch = areaIds.filter((id) => !(id in existing));
    if (toFetch.length === 0) return;
    const results = await Promise.allSettled(
      toFetch.map((id) => structuresGeojsonApi.loadAreaManifest(id))
    );
    const nextManifests = { ...get()._manifestsByArea };
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') nextManifests[toFetch[i]] = r.value;
    });
    set({ _manifestsByArea: nextManifests, _loadedAreaIds: Object.keys(nextManifests) });
    // Fire-and-forget: housekeep cached shards from old manifest generations.
    for (const id of toFetch) {
      const m = nextManifests[id];
      if (m) void evictStaleShards(id, m.generatedAt);
    }
  },

  ensureAreaLoadedForSearch: async (areaId: string) => {
    // Idempotent: a second call for an area already being swept awaits the
    // same promise instead of starting a duplicate one.
    const inflight = get()._searchPreloadInFlight[areaId];
    if (inflight) return inflight;

    const promise = (async () => {
      await get().ensureManifests([areaId]);
      const manifest = get()._manifestsByArea[areaId];
      // Manifest fetch failed, or (defensively) still missing — nothing to
      // sweep.
      if (!manifest) return;
      // Big Bear-sized areas only (see SEARCH_PRELOAD_MAX_STRUCTURES's
      // comment in shardLoader.ts) — a dense area like San Francisco
      // (~224k) must never have its whole shard set queued up just because
      // someone focused the search field.
      if (manifest.totalStructures > SEARCH_PRELOAD_MAX_STRUCTURES) return;

      // Shards this call has already tried and failed, excluded from
      // `selectMissingAreaShards` locally (but NOT removed from state) so a
      // shard that keeps 404ing doesn't spin this loop forever — a later
      // viewport pan through it, or a fresh `ensureAreaLoadedForSearch`
      // call, will retry it via the normal inflight-cleared state.
      const failedKeys = new Set<string>();

      // Batches of at most MAX_SHARDS_PER_LOAD, one after another (not
      // concurrently) — same per-call cap as loadAreasForViewport, just
      // applied repeatedly until the whole area is in memory. Recomputed
      // from live state each iteration so shards a concurrent viewport load
      // grabs in the meantime are skipped here rather than double-fetched:
      // both paths mark a shard inflight in the SAME `_inflightShards` set
      // before fetching it (loadAreasForViewport in Phase 2/3 above, this
      // loop right below), so whichever call claims a shard first is the
      // only one that fetches it.
      //
      // This never touches `_loadGeneration` / `_loadAbortController`, so it
      // can't be cancelled by a viewport load bumping the generation, and it
      // can't cancel one either — the two are independent series of
      // "fetch batch, commit batch" calls over the same inflight set and
      // shard cache, not a single cancellable operation. A user panning
      // while this sweep runs keeps panning at full speed; this sweep keeps
      // filling in the rest of the area at its own pace in the background.
      while (true) {
        const { _loadedShardOrder, _inflightShards } = get();
        const excludeInflight =
          failedKeys.size > 0 ? new Set([..._inflightShards, ...failedKeys]) : _inflightShards;
        const missing = selectMissingAreaShards({
          areaId,
          manifest,
          loadedShardOrder: _loadedShardOrder,
          inflightShards: excludeInflight,
        });
        if (missing.length === 0) return;
        const batch = missing.slice(0, MAX_SHARDS_PER_LOAD);

        const inflightBefore = new Set(get()._inflightShards);
        for (const item of batch) inflightBefore.add(item.key);
        set({ _inflightShards: inflightBefore });

        const { staged, failures } = await fetchShardBatch(get, batch);
        for (const key of failures) failedKeys.add(key);

        const center = get()._lastViewportCenter;
        // Respects MAX_CACHED_STRUCTURES / LRU eviction via the same
        // evictToLimit reuse as loadAreasForViewport. In practice this never
        // evicts anything: SEARCH_PRELOAD_MAX_STRUCTURES (40k) is well under
        // MAX_CACHED_STRUCTURES (150k), so sweeping one area under that cap
        // doesn't push the total over the ceiling on its own. If it ever
        // did (e.g. a huge area already loaded via viewport pans pushed the
        // total close to 150k first), centering eviction on the last known
        // viewport — not this area — keeps whatever's on screen from being
        // the thing that gets dropped.
        commitFetchedShards(set, get, batch, staged, center?.lat ?? 0, center?.lng ?? 0);

        if (failures.length === batch.length && staged.length === 0) {
          // Every shard in this batch failed outright (not just cache
          // misses) — stop rather than spin on a dead network/area.
          log.error(`Search preload: batch of ${batch.length} shards all failed for ${areaId}`);
          return;
        }
      }
    })();

    set({ _searchPreloadInFlight: { ...get()._searchPreloadInFlight, [areaId]: promise } });
    try {
      await promise;
    } finally {
      const next = { ...get()._searchPreloadInFlight };
      delete next[areaId];
      set({ _searchPreloadInFlight: next });
    }
  },

  fetchStructuresByIds: async (ids) => {
    const results: Structure[] = [];
    for (const id of ids) {
      try {
        // Check the in-memory user-added cache first to avoid a Firestore
        // round-trip for freshly added structures.
        const fromUserAdded = findUserAdded(get().userAddedStructures, id);
        const s = fromUserAdded ?? (await structuresApi.getStructure(id));
        if (s) results.push(s);
      } catch {
        // Skip structures that fail to resolve; render whatever loaded.
      }
    }
    return results;
  },

  getStructureById: async (structureId) => {
    // Prefer the live user-added cache (same logic as fetchStructure) to
    // avoid an unnecessary Firestore read for freshly added structures.
    const fromUserAdded = findUserAdded(get().userAddedStructures, structureId);
    return fromUserAdded ?? (await structuresApi.getStructure(structureId));
  },

  getReportRecipients: async (structureId) => {
    return await structureClaimsApi.getReportRecipients(structureId);
  },

  updateReportRecipients: async (structureId, userId, recipients) => {
    await structureClaimsApi.updateReportRecipients(structureId, userId, recipients);
  },

  updateDefaultVisibility: async (structureId, userId, visibility) => {
    await structureClaimsApi.updateDefaultPostVisibility(structureId, userId, visibility);
  },

  getClaimForStructure: async (structureId) => {
    return await structureClaimsApi.getClaim(structureId);
  },

  addUserStructure: async (input: AddUserStructureInput) => {
    const result = await callAddUserStructure(input);

    // The callable defaults `claim` to true server-side (post-attach passes
    // `claim: false` explicitly to add an address without claiming it) —
    // this path had no optimistic patch at all before this fix, so a
    // freshly claimed "Add my property" structure rendered unclaimed on the
    // map until the CF-derived area summary caught up.
    const claims = input.claim !== false;
    const userId = useAuthStore.getState().user?.id;
    if (claims && userId) {
      const summary: MapClaimSummary = { claimedBy: userId };
      set((s) => ({
        _optimisticClaims: {
          ...s._optimisticClaims,
          [result.structureId]: { summary, at: Date.now() },
        },
      }));
      recomputeClaims(set, get);
    }

    return result;
  },

  notifyAgent: async (structureId, status, note) => {
    const result = await structureClaimsApi.notifyAgent(structureId, status, note);

    const evaluationStatusAt = Timestamp.now();
    const { selectedClaim, claims } = get();
    const existing =
      claims[structureId] ?? (selectedClaim?.id === structureId ? selectedClaim : undefined);
    if (existing) {
      const updated: StructureClaim = { ...existing, evaluationStatus: status, evaluationStatusAt };
      set((s) => ({
        claims: { ...s.claims, [structureId]: updated },
        selectedClaim: s.selectedClaim?.id === structureId ? updated : s.selectedClaim,
      }));
    }

    return result;
  },

  evictAreasNotIn: (activeAreaIds: string[]) => {
    const activeSet = new Set(activeAreaIds);
    const state = get();

    // Fast-path: if every currently-loaded area is already in the active set,
    // there's nothing to evict. Avoids a pointless re-render on every
    // `areas` update.
    let needsEviction = false;
    for (const id in state._manifestsByArea) {
      if (!activeSet.has(id)) {
        needsEviction = true;
        break;
      }
    }
    if (!needsEviction) {
      for (const id of state._loadedAreaIds) {
        if (!activeSet.has(id)) {
          needsEviction = true;
          break;
        }
      }
    }
    if (!needsEviction) {
      for (const key of state._loadedShardOrder) {
        const colon = key.indexOf(':');
        const areaId = colon >= 0 ? key.slice(0, colon) : key;
        if (!activeSet.has(areaId)) {
          needsEviction = true;
          break;
        }
      }
    }
    if (!needsEviction) return;

    const nextManifests: Record<string, AreaManifest> = {};
    for (const id in state._manifestsByArea) {
      if (activeSet.has(id)) nextManifests[id] = state._manifestsByArea[id];
    }

    const nextShards: Record<string, Structure[]> = {};
    const nextOrder: string[] = [];
    for (const key of state._loadedShardOrder) {
      const colon = key.indexOf(':');
      const areaId = colon >= 0 ? key.slice(0, colon) : key;
      if (activeSet.has(areaId)) {
        nextShards[key] = state._structuresByShard[key];
        nextOrder.push(key);
      }
    }

    const nextLoadedAreaIds = state._loadedAreaIds.filter((id) => activeSet.has(id));

    set({
      _manifestsByArea: nextManifests,
      _structuresByShard: nextShards,
      _loadedShardOrder: nextOrder,
      _loadedAreaIds: nextLoadedAreaIds,
    });
  },
}));
